import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Attachment, ChatWidgetProps } from './types';
import { useChat } from './hooks/useChat';
import { useResizable } from './hooks/useResizable';
import { useAppTheme } from './hooks/useAppTheme';
import { useLanguage } from '../../../hooks/useLanguage';
import { mergeLabels } from './i18n';
import { cx } from './utils';
import { Composer, type ComposerHandle } from './components/Composer';
import { Launcher } from './components/Launcher';
import { MessageList } from './components/MessageList';
import { ChatIcon, CloseIcon, MaximizeIcon, PlusIcon, RestoreIcon } from './components/icons';
import './styles.css';

const DEFAULTS = {
  width: 400,
  height: 600,
  minWidth: 320,
  minHeight: 380,
  maxFileSize: 10 * 1024 * 1024,
  maxFiles: 5,
};

function renderAvatar(avatar: string | ReactNode | undefined, fallback: ReactNode): ReactNode {
  if (typeof avatar === 'string') return <img className="cbw-avatar__img" src={avatar} alt="" />;
  return avatar ?? fallback;
}

export function ChatWidget(props: ChatWidgetProps) {
  const {
    config,
    theme,
    language,
    placement = 'bottom-right',
    cssVars,
    className,
    avatar,
    botAvatar,
    title,
    subtitle,
    labels: labelOverrides,
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange,
    welcomeMessage,
    suggestedPrompts,
    persist = true,
    storageKey = 'cbw',
    width = DEFAULTS.width,
    height = DEFAULTS.height,
    minWidth = DEFAULTS.minWidth,
    minHeight = DEFAULTS.minHeight,
    resizable = true,
    allowFullscreen = true,
    allowAttachments = true,
    accept,
    maxFileSize = DEFAULTS.maxFileSize,
    maxFiles = DEFAULTS.maxFiles,
    markdown = true,
    renderMessage,
    footer,
  } = props;

  // Follow the application's toggles unless the caller overrides them.
  const appTheme = useAppTheme();
  const { currentLanguage } = useLanguage();
  const resolvedTheme = theme ?? appTheme;
  const resolvedLanguage = language ?? currentLanguage;

  const labels = useMemo(
    () => mergeLabels(resolvedLanguage, labelOverrides),
    [resolvedLanguage, labelOverrides],
  );
  const panelId = useId();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const [fullscreen, setFullscreen] = useState(false);
  const composerRef = useRef<ComposerHandle>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const chat = useChat({
    config,
    persist,
    storageKey,
    welcomeMessage,
    errorMessage: labels.errorGeneric,
    onSend: props.onSend,
    onResponse: props.onResponse,
    onFeedback: props.onFeedback,
    onError: props.onError,
    onConversationChange: props.onConversationChange,
  });

  const defaultSize = useMemo(() => ({ width, height }), [width, height]);
  const { size, isResizing, onHandlePointerDown, resetSize } = useResizable({
    placement,
    defaultSize,
    minWidth,
    minHeight,
    enabled: resizable && !fullscreen,
    persist,
    storageKey,
  });

  // Focus the composer when the panel opens.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => composerRef.current?.focus(), 120);
    return () => clearTimeout(timer);
  }, [open]);

  // Escape closes the panel (or leaves fullscreen first).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (fullscreen) setFullscreen(false);
      else setOpen(false);
    };
    const node = panelRef.current;
    node?.addEventListener('keydown', onKeyDown);
    return () => node?.removeEventListener('keydown', onKeyDown);
  }, [open, fullscreen, setOpen]);

  const handleSend = useCallback(
    (text: string, attachments: Attachment[]) => {
      void chat.send(text, attachments);
    },
    [chat],
  );

  const handleNewChat = useCallback(() => {
    chat.reset();
    composerRef.current?.focus();
  }, [chat]);

  const headerAvatar = renderAvatar(botAvatar ?? avatar, <ChatIcon width={20} height={20} />);
  const messageAvatar = renderAvatar(botAvatar ?? avatar, <ChatIcon width={16} height={16} />);

  const panelStyle = fullscreen ? undefined : { width: size.width, height: size.height };

  return (
    <div
      className={cx('cbw-root', className)}
      data-cbw-theme={resolvedTheme === 'auto' ? undefined : resolvedTheme}
      data-cbw-placement={placement}
      style={cssVars}
    >
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          className={cx(
            'cbw-panel',
            fullscreen && 'cbw-panel--fullscreen',
            isResizing && 'is-resizing',
          )}
          style={panelStyle}
          role="dialog"
          aria-modal="false"
          aria-label={title ?? labels.title}
        >
          {resizable && !fullscreen && (
            <div
              className="cbw-resize"
              onPointerDown={onHandlePointerDown}
              onDoubleClick={resetSize}
              role="separator"
              aria-label={labels.resize}
              aria-orientation="horizontal"
            />
          )}

          <header className="cbw-header">
            <div className="cbw-avatar cbw-avatar--header">{headerAvatar}</div>
            <div className="cbw-header__text">
              <span className="cbw-header__title">{title ?? labels.title}</span>
              {(subtitle ?? labels.subtitle) && (
                <span className="cbw-header__subtitle">{subtitle ?? labels.subtitle}</span>
              )}
            </div>
            <div className="cbw-header__actions">
              <button
                type="button"
                className="cbw-iconbtn cbw-iconbtn--onhead"
                onClick={handleNewChat}
                aria-label={labels.newChat}
                title={labels.newChat}
              >
                <PlusIcon />
              </button>
              {allowFullscreen && (
                <button
                  type="button"
                  className="cbw-iconbtn cbw-iconbtn--onhead"
                  onClick={() => setFullscreen((value) => !value)}
                  aria-label={fullscreen ? labels.restore : labels.maximize}
                  title={fullscreen ? labels.restore : labels.maximize}
                >
                  {fullscreen ? <RestoreIcon /> : <MaximizeIcon />}
                </button>
              )}
              <button
                type="button"
                className="cbw-iconbtn cbw-iconbtn--onhead"
                onClick={() => setOpen(false)}
                aria-label={labels.close}
                title={labels.close}
              >
                <CloseIcon />
              </button>
            </div>
          </header>

          <MessageList
            messages={chat.messages}
            labels={labels}
            markdown={markdown}
            avatar={<div className="cbw-avatar cbw-avatar--msg">{messageAvatar}</div>}
            suggestedPrompts={suggestedPrompts}
            isStreaming={chat.isStreaming}
            renderMessage={renderMessage}
            onVote={chat.vote}
            onSuggestion={(prompt) => composerRef.current?.setText(prompt)}
          />

          <Composer
            handle={composerRef}
            labels={labels}
            disabled={chat.isStreaming}
            allowAttachments={allowAttachments}
            accept={accept}
            maxFileSize={maxFileSize}
            maxFiles={maxFiles}
            onSend={handleSend}
            // Aborting is not only a convenience. Against a backend with a bounded
            // number of concurrent runs, the disconnect is what cancels the run and
            // frees the slot for someone else.
            onStop={chat.stop}
          />

          {footer && <div className="cbw-footer">{footer}</div>}
        </div>
      )}

      <Launcher
        open={open}
        label={open ? labels.launcherClose : labels.launcherOpen}
        avatar={avatar}
        onClick={() => setOpen(!open)}
      />
    </div>
  );
}
